import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from './AuthContext'
import type { PortalRole } from '@/api/types'

/**
 * Route guard.
 *
 * This is a usability boundary, not a security boundary — the API enforces
 * roles independently on every request. Hiding a route the server would reject
 * anyway just avoids showing people doors they cannot open.
 *
 * `fallback` replaces the "not available" card for someone signed in below
 * the role. Used by /field so a technician with certifications switched on is
 * sent to their own; without it, nothing changes.
 */
export function RequireAuth({
  children,
  role,
  fallback,
}: {
  children: ReactNode
  role?: PortalRole
  fallback?: ReactNode
}) {
  const { isAuthenticated, hasRole } = useAuth()
  const location = useLocation()

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (role && !hasRole(role)) {
    return <>{fallback ?? <RoleNotAvailable role={role} />}</>
  }

  return <>{children}</>
}

/** The card shown to someone signed in below a route's role. */
export function RoleNotAvailable({ role }: { role: PortalRole }) {
  const { user } = useAuth()
  return (
    <div className="app-main">
      <div className="alert alert-warn">
        <strong>Not available for your role.</strong>
        <p className="stack">
          You are signed in as <strong>{user?.display_name}</strong> ({user?.role}).
          This area needs {role} access or higher.
        </p>
      </div>
    </div>
  )
}
